/**
 * Reconhecer — modelo de pontuacao fracionada (FRAC-01).
 *
 * Ate esta entrega a categoria ERA o valor: #VALEU = 100, #MANDOUBEM = 200,
 * #UAU = 300. Agora a categoria define uma FAIXA e o autor escolhe o valor
 * dentro dela, de 20 em 20.
 *
 * Modulo puro de proposito: e a unica fonte das faixas no front, e existe para
 * ser testado sem React. A mesma tabela vive na trigger `ER3LOrs` do Funifier —
 * qualquer mudanca aqui precisa acompanhar la.
 */

export type CoinKey = 'valeu' | 'mandou' | 'uau';

export const POINTS_STEP = 20;

/** Teto de pontos que um jogador pode enviar para a MESMA pessoa na fase. */
export const LIMIT_PER_TARGET = 300;

export type CoinTier = {
  label: string;
  /** Menor valor da faixa. */
  min: number;
  /** Maior valor da faixa — o "Ate N pontos" exibido no card. */
  max: number;
};

/**
 * Faixa propria por categoria: cada uma comeca 20 acima do teto da anterior,
 * resultando em exatamente 5 opcoes por categoria.
 */
export const COIN_TIERS: Record<CoinKey, CoinTier> = {
  valeu: { label: '#VALEU', min: 20, max: 100 },
  mandou: { label: '#MANDOUBEM', min: 120, max: 200 },
  uau: { label: '#UAU', min: 220, max: 300 },
};

export const COIN_KEYS: CoinKey[] = ['valeu', 'mandou', 'uau'];

/**
 * Saldo minimo para reconhecer — o menor valor enviavel do programa. Derivado do
 * piso do #VALEU de proposito: era 100 fixo (a categoria mais barata custava
 * 100), e com a fracionacao qualquer piso maior que este bloquearia envios
 * legitimos. Usado tambem pela Dashboard, para decidir se exibe o acesso.
 */
export const MIN_BALANCE_TO_RECOGNIZE = COIN_TIERS.valeu.min;

/**
 * Por que os motivos sao um tipo e nao um booleano: a UI precisa dizer POR QUE
 * a opcao esta bloqueada ("Saldo insuficiente" x "Limite atingido"), e o leitor
 * de tela precisa da mesma informacao.
 */
export type OptionState = 'enabled' | 'no-balance' | 'over-limit';

/** Estado de bloqueio — `OptionState` sem o caso habilitado. */
export type BlockedReason = Exclude<OptionState, 'enabled'>;

export function isCoinKey(value: unknown): value is CoinKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(COIN_TIERS, value);
}

/** As 5 opcoes da faixa, em ordem crescente. Ex.: uau -> [220,240,260,280,300]. */
export function pointOptionsFor(coin: CoinKey): number[] {
  const tier = COIN_TIERS[coin];
  const options: number[] = [];

  for (let value = tier.min; value <= tier.max; value += POINTS_STEP) {
    options.push(value);
  }

  return options;
}

/** Teto da faixa da categoria: o "Ate N pontos" do card e o valor que o backend assume sem escolha. */
export function ceilingFor(coin: CoinKey): number {
  return COIN_TIERS[coin].max;
}

export function isValidPoints(coin: CoinKey, value: unknown): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return false;
  }

  const tier = COIN_TIERS[coin];

  return value >= tier.min && value <= tier.max && value % POINTS_STEP === 0;
}

/**
 * Quanto o autor pode gastar neste envio: limitado pelo saldo dele E pelo que
 * resta do teto de 300 para esta pessoa nesta fase.
 */
export function maxAllowedPoints(balance: number, pointsSent: number): number {
  const safeBalance = Number.isFinite(balance) ? Math.max(0, balance) : 0;
  const safeSent = Number.isFinite(pointsSent) ? Math.max(0, pointsSent) : 0;
  const remainingQuota = Math.max(0, LIMIT_PER_TARGET - safeSent);

  return Math.min(safeBalance, remainingQuota);
}

/**
 * Estado de uma opcao. `no-balance` vence `over-limit` de proposito: sem saldo o
 * jogador nao envia para ninguem hoje; o limite por pessoa ele contorna
 * escolhendo outro colega. A mensagem mais acionavel vem primeiro.
 */
export function optionState(coin: CoinKey, value: number, balance: number, pointsSent: number): OptionState {
  if (!isValidPoints(coin, value)) {
    return 'no-balance';
  }

  const safeBalance = Number.isFinite(balance) ? Math.max(0, balance) : 0;
  const safeSent = Number.isFinite(pointsSent) ? Math.max(0, pointsSent) : 0;

  if (value > safeBalance) {
    return 'no-balance';
  }

  if (safeSent + value > LIMIT_PER_TARGET) {
    return 'over-limit';
  }

  return 'enabled';
}

/**
 * Uma categoria so esta disponivel se ao menos uma das suas opcoes estiver
 * habilitada. Derivado de `optionState` — nao ha como o card ficar clicavel com
 * todos os chips bloqueados.
 */
export function coinIsAvailable(coin: CoinKey, balance: number, pointsSent: number): boolean {
  return pointOptionsFor(coin).some(value => optionState(coin, value, balance, pointsSent) === 'enabled');
}

/** Motivo pelo qual a categoria inteira esta indisponivel — para o rotulo do card. */
export function coinUnavailableReason(coin: CoinKey, balance: number, pointsSent: number): BlockedReason | null {
  if (coinIsAvailable(coin, balance, pointsSent)) {
    return null;
  }

  // O piso da faixa e o caso mais favoravel: se nem ele passa, o motivo dele
  // explica a categoria inteira. Como a categoria esta indisponivel, este
  // `optionState` nunca devolve 'enabled'.
  const reason = optionState(coin, COIN_TIERS[coin].min, balance, pointsSent);

  return reason === 'enabled' ? null : reason;
}

/** Verdadeiro quando nenhuma categoria pode ser enviada — mensagem de saldo esgotado. */
export function hasAnyRecognitionAvailable(balance: number, pointsSent: number): boolean {
  return COIN_KEYS.some(coin => coinIsAvailable(coin, balance, pointsSent));
}
